import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, GraduationCap } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { SubjectIcon, subjectGradient } from "@/components/SubjectIcon";
import { TIDS } from "@/constants/testIds";

const CLASS_GRADIENTS = [
    "from-rose-500 to-pink-500",
    "from-orange-500 to-amber-500",
    "from-yellow-500 to-amber-400",
    "from-emerald-500 to-teal-500",
    "from-cyan-500 to-blue-500",
    "from-indigo-500 to-violet-500",
    "from-fuchsia-500 to-purple-500",
    "from-primary to-secondary",
];

export default function Classes() {
    const { t, subjectLabel, lang } = useApp();
    const [classes, setClasses] = useState([]);

    useEffect(() => {
        api.classes().then(setClasses).catch(() => {});
    }, []);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:pb-32">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">{t.browse}</p>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t.classes}</h1>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {classes.map((c, i) => (
                    <motion.div
                        key={c.class}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                    >
                        <Link
                            to={`/classes/${c.class}`}
                            data-testid={TIDS.classCard(c.class)}
                            className="group relative overflow-hidden block rounded-3xl bg-card border border-border/70 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
                        >
                            <div className={`absolute -top-10 -right-10 h-36 w-36 rounded-full bg-gradient-to-br ${CLASS_GRADIENTS[(c.class - 1) % 8]} opacity-20 blur-3xl`} />
                            <div className="relative flex items-start justify-between">
                                <div>
                                    <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${CLASS_GRADIENTS[(c.class - 1) % 8]} grid place-items-center text-white shadow-md mb-4`}>
                                        <GraduationCap className="h-6 w-6" />
                                    </div>
                                    <div className="font-display font-extrabold text-2xl tracking-tight">{t.classN(c.class)}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        {c.subject_count} {t.subjects.toLowerCase()} • {c.lo_count} {t.loCount}
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <div className="relative flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-border/60">
                                {c.subjects.slice(0, 4).map((s) => (
                                    <div
                                        key={s}
                                        className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-[11px] font-medium"
                                    >
                                        <SubjectIcon subject={s} className="h-3 w-3" />
                                        <span className={lang === "od" ? "font-odia" : ""}>{subjectLabel(s)}</span>
                                    </div>
                                ))}
                                {c.subjects.length > 4 && (
                                    <div className="px-2 py-1 rounded-full bg-muted text-[11px] font-medium">+{c.subjects.length - 4}</div>
                                )}
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}
